<template>
    <PlanoScaffold v-if="loading">
        <Loader :loading="true"></Loader>
        <PlanoHeader>
            <OutlineButton slot="trailing" @click="exit();">{{ $t("plano.editor.header.file.exit") }}</OutlineButton>
        </PlanoHeader>
        <PlanoNav exitTo="/planos"></PlanoNav>
    </PlanoScaffold>
    <PlanoScaffold v-else-if="planoNotFound">
        <PlanoHeader>
            <OutlineButton slot="trailing" @click="exit();">{{ $t("plano.editor.header.file.exit") }}</OutlineButton>
        </PlanoHeader>
        <PlanoNav exitTo="/planos"></PlanoNav>
        <Error codeError="404"></Error>
    </PlanoScaffold>
    <!-- currently, only the editor can be locked. -->
    <PlanoScaffold v-else :datas="plano" @rename-favorite="renameFavorite">
        <Loader :loading="saving || rasterizing"></Loader>
        <Lock :planoId="planoId" ref="lock"></Lock>
        <PlanoHeader :plano="plano" :logoUrl="logoUrl">
            <OutlineButton v-if="!isReadOnly" slot="trailing" @click="e => save()">{{ $t("plano.editor.header.file.save") }}</OutlineButton>
            <OutlineButton slot="trailing" @click="exit();">{{ $t("plano.editor.header.file.exit") }}</OutlineButton>
        </PlanoHeader>
        <PlanoNav
            :locked="isReadOnly"
            exitTo="/planos"
            :plano="plano"
            @exportPdf="exportPdf"
            @exportXls="exportXls"
            @nameChanged="nameChanged"
            @save="save"
            @saveAs="toggleModalSaveAs(true);"
            @saveAsTemplate="toggleModalSaveAsTemplate(true);"
        ></PlanoNav>
        <LockStatus v-if="lockDetails" :lockDetails="lockDetails" :locked="!isOwned" :readOnly="isReadOnly"></LockStatus>
        <PlanoEngine
            :key="planoId"
            :catalogPanes="catalogPanes"
            :catalogItems="catalogItems.value"
            :systemType="plano && plano.template.systemType.id"
            :brandId="plano && plano.brand.id"
            :zoneId="plano && plano.zone.id"
            :campaignId="plano && plano.campaign.id"
            @search-changed="searchChanged"
            @save="e => save()"
        ></PlanoEngine>
        <InfosBar v-if="currentPlano" :plano="currentPlano"></InfosBar>
        <ModalExitPlano
            v-if="showExitModal"
            @save="saveAfterExit($event);"
            @close="closeExitModal();"
            :name="plano.name"
            :readOnly="readOnly"
        ></ModalExitPlano>
        <ModalSaveAsPlano v-if="showModalSaveAs && plano" :plano="plano" @exit="exit" @close="toggleModalSaveAs(false);"></ModalSaveAsPlano>
        <ModalExportPdf v-if="showModalExportPdf && currentPlano" :plano="currentPlano" @close="toggleModalExportPdf(false);"></ModalExportPdf>
        <ModalSavePlanoAsToTemplate
            v-if="showModalSaveAsTemplate && plano"
            @close="toggleModalSaveAsTemplate(false);"
            :plano="plano"
        ></ModalSavePlanoAsToTemplate>
    </PlanoScaffold>
</template>

<script>
import PlanoNext, { load as loadPlanogram, save, Tools, Context, Toolkit } from "plano_engine";
import planoService from "@/services/plano";
import displayerService from "@/services/displayer";
import componentService, { PLANO_STEP } from "@/services/component";
import favoriteService from "@/services/favorites";
import exportService from "@/services/export";
import shadeService from "@/services/shades";
import PlanoHeader from "./plano-header";
import PlanoNav from "./plano-nav";
import PlanoScaffold from "./plano-scaffold";
import PlanoEngine from "./favorite-plano-engine";
import Globals from "@/components/globals";
import OutlineButton from "./components/outline-button";
import Loader from "@/components/loader/loader";
import Lock from "./lock";
import ModalExitPlano from "./modal/ModalExitPlano";
import ModalSaveAsPlano from "./modal/ModalSaveAsPlano";
import ModalSavePlanoAsToTemplate from "@/plano/modal/ModalSavePlanoAsTemplate";
import ExitPlanoBuilderMixin from "@/mixins/ExitPlanoBuilderMixin";
import TryLoadMixin, { TrySaveMixin, createMixin } from "@/mixins/TryLoadMixin";
import LockStatus from "./components/lock-status";
import InfosBar from "./components/infos-bar";
import ModalExportPdf from "./modal/ModalExportPdf";
import Error from "@/components/error/Error";
import { mapGetters } from "vuex";
import { bus } from "@/main";
import cacheService from "@/services/cache";

import {
    allPane,
    furniturePane,
    modulesPane,
    structurePane,
    blankingGraphicPane,
    applyDetailsToModuleElements,
    PaginationMixin,
    getAllShadeIdForModuleElements,
    getAllModuleIdFromModuleElements,
    centerCarcass,
    findAllComponentsSpecByType,
    injectElementDetailsInModels,
    COMPONENT_TYPE,
    CORRUPTED_MODULE_ORIGIN,
    visitSpecs,
    updateHihlightDetailsInModuleSpecs,
    addCommentTipsInPlanogramme,
    visualsModelFromPlanogram,
    cleanClipRefElementsInScratchpad
} from "./utils";

function findAllModuleElements() {
    return [...Context.visualizer.observers].filter(element => {
        return element instanceof BoardModuleElement;
    });
}
const PLANO_STATUS = {
    SUBMITTED: "SUBMITTED",
    APPROVED: "APPROVED"
};

function updateCurrentPlano() {
    if (this.currentPlano) {
        this.currentPlano.config = Context.save();
    }
}

class NotFoundError {
    constructor(error) {
        this.error = error;
    }
}

export default {
    mixins: [ExitPlanoBuilderMixin, TryLoadMixin, TrySaveMixin, createMixin("rasterizing", "tryRasterize"), PaginationMixin],
    components: {
        Error,
        PlanoNext,
        PlanoHeader,
        PlanoNav,
        PlanoScaffold,
        PlanoEngine,
        OutlineButton,
        Loader,
        LockStatus,
        Lock,
        ModalSaveAsPlano,
        ModalExitPlano,
        InfosBar,
        ModalExportPdf,
        ModalSavePlanoAsToTemplate
    },
    data: () => ({
        planoNotFound: false,
        isMounted: false,
        plano: undefined,
        currentPlano: undefined,
        catalogPanes: { allPane, modulesPane, furniturePane, blankingGraphicPane, structurePane },
        showModalSaveAs: false,
        showModalExportPdf: false,
        showModalSaveAsTemplate: false,
        observer: undefined,
        listCorruptedModules: new Set()
    }),
    watch: {
        loading() {
            this.checkSavedOnExit = !this.loading && !this.planoNotFound;
        },
        planoNotFound() {
            this.checkSavedOnExit = !this.loading && !this.planoNotFound;
        },
        planoId(current, old) {
            if (current != old) this.fetchAll();
        },
        isReadOnly() {
            this.readOnly = this.isReadOnly;
        }
    },
    created() {
        this.readOnly = this.isReadOnly;
        // Chargement du planogramme
        this.fetchAll();
        this.observer = new MutationObserver(() => {
            updateCurrentPlano.call(this);
        });

        const highlightColors = (this.currentBrand.brandHightlighColorByBrand || []).map(highlight => highlight.code);
        if (highlightColors.length) {
            Context.highlightColors = highlightColors;
        }

        Context.isFixable = this.isElementNotInTemplate.bind(this);
        window.addEventListener("missing-plano-module", this.moduleMissingImageListener);
    },
    mounted() {
        bus.$on("focus-plano", this.focusPlanoListener);
        this.isMounted = true;
    },
    methods: {
        toggleModalSaveAsTemplate(value) {
            this.showModalSaveAsTemplate = value;
        },
        focusPlanoListener() {
            // Emit an event to plano_engine via the window object
            let e = new Event("focus-plano");
            window.dispatchEvent(e);
        },
        moduleMissingImageListener({ origin, module, imageURL }) {
            this.listCorruptedModules.add({ origin, module, imageURL });
        },
        showCorruptedModulesModal() {
            for (const imageURL in Context.svgImages) {
                if (Context.svgImages[imageURL] === null) {
                    const module = this.modulesInCatalogImagesHashMap.get(imageURL);
                    this.listCorruptedModules.add({ origin: CORRUPTED_MODULE_ORIGIN.catalog, module, imageURL });
                }
            }
            if (this.listCorruptedModules.size === 0) return;

            const messageTitle = `<h2> <a href="#" style="color: #2d2d2d">${this.trads.corruptedModulesModal.subTitle}</a></h2>`;
            const sortedCorruptedModules = [...this.listCorruptedModules].sort(a => (a.origin === CORRUPTED_MODULE_ORIGIN.planogram ? -1 : 1));
            let message = messageTitle;

            for (const corruptedModule of sortedCorruptedModules) {
                /** @type corruptedModule = { module: Object,  origin: "planogram"|"catalog",imageURL: String|undefined} */
                const { module, origin } = corruptedModule;
                const boldLine = origin === CORRUPTED_MODULE_ORIGIN.planogram ? "bold" : "unset";
                if (module) {
                    const moduleMessage = module.missing
                        ? this.trads.corruptedModulesModal.missingModuleMessage
                        : `${this.$tradFor(module.terms.name)} - ${this.$trad.common[origin]}`;
                    message += `<li style='text-align:left;font-weight:${boldLine};'>
                        (${module.id}) ${moduleMessage}
                    </li>`;
                }
            }
            if (sortedCorruptedModules.length > 0 && message !== messageTitle)
                this.dialogWarning(null, this.trads.corruptedModulesModal.title, message);
        },
        isElementNotInTemplate(element) {
            if (!this.displayer || !this.displayer.config) return true;

            let res = true;
            visitSpecs(this.displayer.config.content.paper, spec => {
                if (spec.id === element.id) {
                    res = false;
                }
            });
            return res;
        },
        renameFavorite(builder) {
            favoriteService.renameAt(builder.getId(), {
                name: builder.labelSpec,
                brandId: this.plano.brand.id,
                zoneId: this.plano.zone.id,
                campaignId: this.plano.campaign.id
            });
        },
        toggleModalSaveAs(value) {
            this.showModalSaveAs = value;
        },
        toggleModalExportPdf(value) {
            this.showModalExportPdf = value;
        },
        exportPdf() {
            this.showModalExportPdf = true;
        },
        async exportXls() {
            const url = await this.trySave(() => exportService.asXsl(this.plano.id));
            window.open(`${Globals.getBaseUrl()}${decodeURIComponent(url.path)}`, "_blank");
        },
        async nameChanged(name) {
            try {
                await planoService.editNamePlano(this.planoId, name);
                this.dialogSuccess(this.$t("saved"));
                this.plano = { ...this.plano, name };
            } catch (err) {
                console.error(err);
                this.dialogErrorToast((err && err.message) || err);
                throw err;
            }
        },
        // obtains a plano and insert the template if it's the first opening of the plano
        async fetchPlano() {
            let version = this.$route.query && this.$route.query.version;
            const plano = await planoService.get(this.planoId, version);
            const displayer = await displayerService.get(plano.template.id);
            if (!plano.config) {
                plano.config = displayer.config;
            }
            return [plano, displayer];
        },
        fetchElementsDetailsAndShades(elements) {
            const visualsModels = [...visualsModelFromPlanogram(this.plano.config.models)];
            const elementsIds = getAllModuleIdFromModuleElements(elements);
            const shadeIds = getAllShadeIdForModuleElements(elements.filter(el => el.model.type == "ModuleElement"));
            return Promise.all([
                componentService.fromIds([...elementsIds, ...visualsModels.filter(model => Number.isInteger(model))], {
                    brandId: this.plano.brand.id,
                    zoneId: this.plano.zone.id,
                    campaignId: this.plano.campaign.id
                }),
                shadeService.fromIds([...shadeIds], {
                    brandId: this.plano.brand.id,
                    zoneId: this.plano.zone.id,
                    campaignId: this.plano.campaign.id
                })
            ]);
        },
        async fetchAll() {
            const [plano, moduleSpecs, modulesDetails, shades] = await this.tryLoad(
                async () => {
                    // both shades and components fetch depends on plano, so we must wait fetchPlano first
                    // Chargement du planogramme depuis l'API
                    const resultFetch = await this.fetchPlano().catch(err => {
                        throw new NotFoundError(err);
                    });

                    // On reçoit depuis l'API, le planogramme et son displayer
                    const [plano, displayer] = resultFetch;
                    this.plano = plano;
                    this.displayer = displayer;

                    // Tous les éléments apartenant au displayer sont lockés
                    if (displayer && displayer.config) {
                        visitSpecs(displayer.config.content.paper, spec => {
                            spec.locked = true;
                        });
                    }

                    // Charge les éléments de la bibliothèque en prenant brand, zone, campagne et systemType comme paramètre
                    this.setupCatalogPagination({
                        step: PLANO_STEP.plano,
                        systemTypeId: this.systemType,
                        // the user may have opened a plano from a different brand/zone than the one currently selected
                        // so we use values from the plano instead of local storage.
                        brandId: this.plano.brand.id,
                        zoneId: this.plano.zone.id,
                        campaignId: this.plano.campaign.id
                    });

                    let moduleSpecs = await findAllComponentsSpecByType(plano.config, [COMPONENT_TYPE.module], { includeScratchpad: true });

                    // Met à jour les hightlights de teinte des modules (Attention: PAS les highlights des MODULES)
                    updateHihlightDetailsInModuleSpecs(this.currentBrand.brandHightlighColorByBrand, moduleSpecs);

                    // Récupère les détails des éléments (modules, visuels, etc) depuis l'API
                    const [modulesDetails, shades] = await this.fetchElementsDetailsAndShades(moduleSpecs);

                    // Met à jour le JSON du planogramme avec les informations chargées juste au dessus
                    injectElementDetailsInModels(plano.config, modulesDetails);

                    // Enlève le lien caddy / fixing pour les caddies se trouvant dans le scratchpad.
                    // (N'étant du coup accroché à aucun Fixing)
                    cleanClipRefElementsInScratchpad(plano.config);
                    injectElementDetailsInModels(plano.config, modulesDetails);
                    return [plano, moduleSpecs, modulesDetails, shades];
                },
                (err, cb) => {
                    if (err instanceof NotFoundError) {
                        this.planoNotFound = true;
                        return;
                    }
                    cb();
                }
            );

            await this.tryRasterize(async () => {
                // Charge le planogramme dans le moteur graphoque
                await loadPlanogram(plano.config);
                centerCarcass();

                const moduleElements = findAllModuleElements().filter(e => {
                    return moduleSpecs.some(spec => spec.model.id === e.modelId());
                });

                // Ajoute la petite icone de commentaire sur les éléments du planogramme
                const elementsInPlanogramme = [...Context.visualizer.observers];
                addCommentTipsInPlanogramme(elementsInPlanogramme, this.plano);

                await Promise.delayed(1000);

                // Rajoute les derniers détails manquants(teinte, icone de status,etc.) dans chaque élément
                await applyDetailsToModuleElements(moduleElements, modulesDetails, shades);
            });

            // Affiche la modal de modules Corrompus
            this.showCorruptedModulesModal();
            bus.$emit("focus-plano");
            this.initialPlano = Context.save();
            this.currentPlano = { ...this.plano, config: Context.save() };
            this.observer.observe(Context.table.paper.root.node, {
                attributes: true,
                childList: true,
                subtree: true
            });
        },
        exit() {
            this.$router.push("/planos");
        },
        async save({ message, exit } = { message: true, exit: false }) {
            await this.trySave(async () => {
                this.plano = await planoService.saveContent(this.planoId, save());
                if (message) this.dialogSuccess(this.$t("saved"));

                //Context.save() (ou save() ) renvoi un snapshot du planogramme comme on le voit sous la forme d'un objet Js
                this.initialPlano = Context.save();
                if (exit) {
                    this.exit();
                }
            });
        }
    },
    computed: {
        ...mapGetters("currentUser", ["currentBrand"]),
        systemType() {
            return this.plano && this.plano.template && this.plano.template.systemType && this.plano.template.systemType.id;
        },
        lock() {
            // required to force vue to watch refs.
            // see https://stackoverflow.com/questions/43531755/using-refs-in-a-computed-property
            this.isMounted;
            this.loading;
            this.rasterizing;

            return this.$refs && this.$refs.lock;
        },
        isOwned() {
            return this.lock && this.lock.isOwned;
        },
        lockDetails() {
            return this.lock && this.lock.details;
        },
        isReadOnly() {
            return !this.isOwned || (this.plano && (this.plano.status === PLANO_STATUS.APPROVED || this.plano.status === PLANO_STATUS.SUBMITTED));
        },
        planoId() {
            return Number.parseInt(this.$route.params.planoId);
        },
        trads() {
            return this.$trad.plano.editor;
        },
        logoUrl() {
            if (!this.plano) {
                return undefined;
            }
            return `${Globals.getBaseUrl()}${this.plano.brand.logoPath}`;
        }
    },
    beforeDestroy() {
        bus.$off("focus-plano", this.focusPlanoListener);
        window.removeEventListener("missing-plano-module", this.moduleMissingImageListener);
        Context.isFixable = undefined;
        this.observer.disconnect();
        Context.endOfCatalogContent = undefined;
    }
};
</script>

<style lang="scss" scoped>
@import "../assets/css/themes";

article {
    flex-grow: 1;
    background-color: rgb(240, 240, 240);
}
</style>



// WEBPACK FOOTER //
// editor.vue?3e039921